import React, { useEffect, useState } from 'react';
import { Image, Button } from 'antd';
import { Modal } from 'pink-lava-ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { usePreviewContext } from '@/context/previewContext';

type PreviewContent = React.ReactNode | string | string[];

interface ContentRendererProps {
  content: PreviewContent;
  fullScreen: boolean;
  description: React.ReactNode;
  isOpen: boolean;
}

const ContentRenderer: React.FC<ContentRendererProps> = ({ content, description, isOpen }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [infoPanelHidden, setInfoPanelHidden] = useState(false);
  const { isMobile } = useBreakpoint();

  if (!content) return null;

  if (!Array.isArray(content))
    return <div className="flex items-center justify-center h-full w-full bg-white">{content}</div>;

  // Array of string URLs (legacy support)
  const imageUrls = content as string[];
  const currentUrl = imageUrls[currentIndex];

  const infoHeight = isOpen ? (isMobile ? '56px' : imageUrls.length > 1 ? '220px' : '124px') : false;

  useEffect(() => {
    if (isOpen) {
      setInfoPanelHidden(false);
      setCurrentIndex(0);
    }
  }, [isOpen]);

  if (infoHeight === false) return;

  return (
    <>
      <div className="relative flex-1 w-full flex items-center" onClick={() => setInfoPanelHidden(!infoPanelHidden)}>
        <div className="flex-1 flex justify-center items-center">
          {/* Left navigation button - always reserve space */}
          {imageUrls.length > 1 && currentIndex > 0 && (
            <div
              className="absolute left-0 pl-4 z-20 pr-10 top-0 h-full bg-linear-to-r from-black/50 to-transparent flex justify-center items-center"
              style={{ maxHeight: `calc(100dvh - ${infoHeight})` }}
            >
              <Button
                shape="circle"
                className="w-full h-full"
                onClick={e => {
                  e.stopPropagation();
                  setCurrentIndex(currentIndex - 1);
                }}
                disabled={imageUrls.length <= 1}
              >
                <ChevronLeft />
              </Button>
            </div>
          )}
          {/* Image container with consistent positioning */}
          <div className="flex justify-center items-center">
            <Image
              src={currentUrl}
              alt={`Image ${currentIndex + 1}`}
              preview={false}
              className="object-contain text-white"
              style={{ maxHeight: `calc(100dvh - ${infoHeight})` }}
            />
          </div>
          {/* Right navigation button - always reserve space */}
          {imageUrls.length > 1 && currentIndex < imageUrls.length - 1 && (
            <div
              className="absolute right-0 top-0 pr-4 z-20 pl-10 h-full bg-linear-to-l from-black/50 to-transparent flex justify-center items-center"
              style={{ maxHeight: `calc(100dvh - ${infoHeight})` }}
            >
              <Button
                shape="circle"
                className="w-full h-full"
                onClick={e => {
                  e.stopPropagation();
                  setCurrentIndex(currentIndex + 1);
                }}
                disabled={imageUrls.length <= 1}
              >
                <ChevronRight />
              </Button>
            </div>
          )}
        </div>
      </div>
      <div
        className={`${isMobile ? 'absolute bottom-0 bg-black/60 ' : 'bg-black/50 '}z-20 w-full transition-all duration-300 ease-out ${
          infoPanelHidden && isMobile
            ? 'opacity-0 translate-y-4 pointer-events-none'
            : 'opacity-100 translate-y-0 pointer-events-auto'
        }`}
        aria-hidden={infoPanelHidden}
      >
        {description && <div className="px-4 py-2">{description}</div>}
        {imageUrls.length > 1 && (
          <div className="flex gap-2 justify-center flex-wrap p-4 pt-0 rounded-lg max-h-32 overflow-y-auto">
            {imageUrls.map((url, index) => (
              <div
                key={index}
                className={`flex flex-col items-center cursor-pointer rounded-md transition-all duration-300 border-2 hover:shadow-lg ${
                  index === currentIndex ? 'border-pink-500 shadow-md' : 'border-transparent'
                }`}
                onClick={() => setCurrentIndex(index)}
              >
                <img src={url} alt={`Thumbnail ${index + 1}`} className="w-15 h-15 object-cover rounded" />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

const PreviewModal: React.FC = () => {
  const { isOpen, content, title, close, fullscreen, description } = usePreviewContext();
  const { isMobile } = useBreakpoint();

  const isFullScreen = fullscreen || isMobile;

  if (!content) return null;

  return (
    <Modal
      open={isOpen}
      onCancel={close}
      footer={() => <></>}
      title={<div className={isFullScreen ? 'pt-3 px-4' : ''}>{title}</div>}
      width={'auto'}
      classNames={{
        content: isFullScreen ? '!w-[100vw] !h-[100dvh] p-0! !rounded-none overflow-hidden' : undefined,
        body: isFullScreen
          ? 'flex flex-col h-[calc(100dvh-56px)]! w-full! bg-black'
          : 'flex flex-col h-[calc(100%-56px)]! w-full! bg-black',
      }}
      className={
        isFullScreen ? '!fixed !inset-0 !z-[1000] !m-0 !p-0 !max-w-none !max-h-none !w-screen !h-[100dvh]' : ''
      }
      centered={!isFullScreen}
      maskClosable
      keyboard
    >
      <ContentRenderer content={content} fullScreen={isFullScreen} description={description} isOpen={isOpen} />
    </Modal>
  );
};

export default PreviewModal;
